/**
 * SCK-606 slice B — NDA acceptance landing page.
 * SCK-704 — render NDA body as Markdown; refuse to display an SPA
 *          index.html payload that could leak in via a mis-configured
 *          textUrl.
 *
 * Flow:
 *   1. Any protected fetch in helpers/actions.js catches
 *      403 {"error":"nda_required","currentVersion":"<v>"} from coord,
 *      stashes the caller's URL in sessionStorage under
 *      "c2a:nda:pending", and redirects the tab to /nda.
 *   2. This page GETs /api/nda/current (unauthenticated allowed on
 *      coord — whitelisted) to render the current text + version.
 *   3. User ticks the checkbox + POSTs /api/nda/accept (authenticated,
 *      bypasses the NDA gate on coord). On 204 we read the pending
 *      destination and navigate there, or fall back to "/" so the app
 *      re-renders + refetches naturally.
 *
 * Uses fetch directly (not helpers/actions.js) so the interceptor's
 * redirect-to-/nda cannot recurse if coord accidentally 403s here.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from "@mui/material";

import { API_URL } from "../helpers/constants";
import { getToken } from "../helpers/tokenStorage";

const NDA_PENDING_KEY = "c2a:nda:pending";

// SCK-704 smoke test — coord serves the NDA as text/markdown from
// /api/nda/text. Prior to that, textUrl defaulted to /legal/nda, a
// phantom SPA route, and the browser was handed index.html which the
// modal then rendered as the NDA body. If we ever see that payload
// again (mis-configured deployment, cache poisoning), refuse to render
// it rather than showing a wall of HTML tags.
const looksLikeSpaShell = (body) =>
  typeof body === "string" && /^\s*<!doctype html>/i.test(body);

const readPendingDestination = () => {
  try {
    const raw = sessionStorage.getItem(NDA_PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.path === "string" ? parsed.path : null;
  } catch {
    return null;
  }
};

const clearPendingDestination = () => {
  try {
    sessionStorage.removeItem(NDA_PENDING_KEY);
  } catch {
    /* ignore */
  }
};

// Scoped markdown renderers so the NDA body sits well inside the
// scrollable Paper region — headings capped so a huge H1 does not blow
// out the modal, `pre` blocks scroll horizontally, links open in a new
// tab. Mirrors the MD_COMPONENTS pattern from
// src/features/support/ChatMessage.js (established SCK-686).
const MD_COMPONENTS = {
  h1: ({ node, ...p }) => (
    <Typography component="h1" sx={{ fontWeight: 600, fontSize: "1.1rem", mt: 1.5, mb: 0.75 }} {...p} />
  ),
  h2: ({ node, ...p }) => (
    <Typography component="h2" sx={{ fontWeight: 600, fontSize: "1rem", mt: 1.25, mb: 0.5 }} {...p} />
  ),
  h3: ({ node, ...p }) => (
    <Typography component="h3" sx={{ fontWeight: 600, fontSize: "0.95rem", mt: 1, mb: 0.5 }} {...p} />
  ),
  h4: ({ node, ...p }) => (
    <Typography component="h4" sx={{ fontWeight: 600, fontSize: "0.9rem", mt: 0.75, mb: 0.25 }} {...p} />
  ),
  p: ({ node, ...p }) => (
    <Typography component="p" sx={{ fontSize: "0.875rem", lineHeight: 1.5, my: 0.75 }} {...p} />
  ),
  a: ({ node, ...p }) => (
    <a
      {...p}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "inherit", textDecoration: "underline" }}
    />
  ),
  ul: ({ node, ordered, ...p }) => (
    <Box component="ul" sx={{ my: 0.5, pl: 3 }} {...p} />
  ),
  ol: ({ node, ordered, ...p }) => (
    <Box component="ol" sx={{ my: 0.5, pl: 3 }} {...p} />
  ),
  li: ({ node, ordered, checked, ...p }) => (
    <Box component="li" sx={{ my: 0.25, fontSize: "0.875rem", lineHeight: 1.5 }} {...p} />
  ),
  code: ({ node, inline, className, children, ...p }) =>
    inline ? (
      <Box
        component="code"
        sx={{
          px: 0.5,
          py: "1px",
          borderRadius: 0.5,
          bgcolor: "action.hover",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: "0.85em",
        }}
        {...p}
      >
        {children}
      </Box>
    ) : (
      <Box
        component="code"
        sx={{
          display: "block",
          p: 1,
          borderRadius: 1,
          bgcolor: "action.hover",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: "0.85em",
          overflowX: "auto",
          whiteSpace: "pre",
        }}
        {...p}
      >
        {children}
      </Box>
    ),
  pre: ({ node, ...p }) => (
    <Box component="pre" sx={{ my: 0.75, overflowX: "auto" }} {...p} />
  ),
  hr: () => <Box sx={{ borderTop: 1, borderColor: "divider", my: 1, opacity: 0.5 }} />,
  blockquote: ({ node, children, ...p }) => {
    // GitHub-flavoured "alert" callouts (e.g. `> [!NOTE]\n> body`) are
    // NOT natively parsed by react-markdown v9 + remark-gfm v4 — they
    // arrive here as an ordinary blockquote whose first paragraph's
    // first text node carries the literal `[!TYPE]` marker. Detect
    // and re-render as an MUI <Alert>, otherwise fall through to the
    // vanilla styled blockquote.
    const ALERT_SEVERITY = {
      NOTE: "info",
      TIP: "success",
      IMPORTANT: "warning",
      WARNING: "warning",
      CAUTION: "error",
    };
    const MARKER = /^\s*\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*/i;

    // Normalise to array; react-markdown often wraps blockquote content
    // in whitespace text nodes between block children.
    const kids = React.Children.toArray(children);

    // Locate the first non-whitespace child.
    const firstIdx = kids.findIndex(
      (c) => !(typeof c === "string" && /^\s*$/.test(c))
    );
    const first = firstIdx >= 0 ? kids[firstIdx] : null;

    const readMarker = () => {
      // Case (a): first child is a <p> element from react-markdown.
      if (React.isValidElement(first)) {
        const grand = React.Children.toArray(first.props?.children ?? []);
        const firstGrand = grand[0];
        if (typeof firstGrand === "string") {
          const m = firstGrand.match(MARKER);
          if (m) {
            const rest = firstGrand.replace(MARKER, "");
            const remainingGrandchildren = rest.length
              ? [rest, ...grand.slice(1)]
              : grand.slice(1);
            const rewrittenFirst = React.cloneElement(
              first,
              first.props,
              remainingGrandchildren
            );
            const remainingBlocks = [
              rewrittenFirst,
              ...kids.slice(firstIdx + 1),
            ];
            return { type: m[1].toUpperCase(), content: remainingBlocks };
          }
        }
      }
      // Case (b): first child is a raw text node.
      if (typeof first === "string") {
        const m = first.match(MARKER);
        if (m) {
          const rest = first.replace(MARKER, "");
          const remainingBlocks = [rest, ...kids.slice(firstIdx + 1)];
          return { type: m[1].toUpperCase(), content: remainingBlocks };
        }
      }
      return null;
    };

    const marker = readMarker();
    if (marker) {
      const severity = ALERT_SEVERITY[marker.type] || "info";
      return (
        <Alert
          severity={severity}
          data-testid={`nda-alert-${marker.type.toLowerCase()}`}
          sx={{ my: 1, "& p": { my: 0.25 } }}
        >
          {marker.content}
        </Alert>
      );
    }

    return (
      <Box
        component="blockquote"
        sx={{
          borderLeft: 3,
          borderColor: "divider",
          pl: 1,
          ml: 0,
          my: 0.75,
          opacity: 0.85,
        }}
        {...p}
      >
        {children}
      </Box>
    );
  },
  table: ({ node, ...p }) => (
    <Box sx={{ overflowX: "auto", my: 0.75 }}>
      <Box component="table" sx={{ borderCollapse: "collapse", fontSize: "0.85rem" }} {...p} />
    </Box>
  ),
  th: ({ node, ...p }) => (
    <Box component="th" sx={{ border: 1, borderColor: "divider", px: 0.75, py: 0.25, fontWeight: 600, textAlign: "left" }} {...p} />
  ),
  td: ({ node, ...p }) => (
    <Box component="td" sx={{ border: 1, borderColor: "divider", px: 0.75, py: 0.25 }} {...p} />
  ),
};

export default function AcceptNdaPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nda, setNda] = useState(null); // { version, effectiveDate, textUrl }
  const [text, setText] = useState("");
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // SCK-704 — if coord ever hands us the SPA shell (mis-configured
  // textUrl, upstream mishap), don't render it as the NDA body. The
  // acceptance button stays clickable — the NDA text is a courtesy
  // view; the acceptance itself binds via the version returned by
  // /api/nda/current. But warn the user that the body did not load.
  const bodyIsSpaShell = useMemo(() => looksLikeSpaShell(text), [text]);
  const displayText = bodyIsSpaShell ? "" : text;

  // Step 1: fetch the current NDA metadata (public endpoint).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/nda/current`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) {
          throw new Error(`Failed to load NDA (HTTP ${res.status})`);
        }
        const payload = await res.json();
        if (cancelled) return;
        setNda(payload);

        // Optional: fetch the text body when a URL is provided so the
        // user can read the agreement inline. If the fetch fails we
        // still let them proceed to the accept action; the textUrl
        // link stays visible as a fallback.
        if (payload?.textUrl) {
          try {
            const txtRes = await fetch(payload.textUrl);
            if (txtRes.ok) {
              const body = await txtRes.text();
              if (!cancelled) {
                if (looksLikeSpaShell(body)) {
                  // Belt-and-suspenders against a future regression of
                  // the SCK-704 phantom-route bug. Log loudly; the
                  // Alert below shows the user why nothing rendered.
                  // eslint-disable-next-line no-console
                  console.warn(
                    "[nda] textUrl=%s returned SPA shell; refusing to render as NDA body",
                    payload.textUrl
                  );
                }
                setText(body);
              }
            }
          } catch {
            /* leave text empty; a load error here surfaces as the
               "NDA text is loading" Alert below, not as a crash */
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load NDA");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAccept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/nda/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (res.status !== 204 && !res.ok) {
        throw new Error(`Accept failed (HTTP ${res.status})`);
      }
      const dest = readPendingDestination();
      clearPendingDestination();
      // Prefer the stashed destination; fall back to "/" so the app
      // shell re-renders and the original view refetches with the
      // gate now cleared.
      navigate(dest && dest !== "/nda" ? dest : "/", { replace: true });
    } catch (e) {
      setError(e.message || "Failed to record NDA acceptance");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card sx={{ width: "min(720px, 92vw)", boxShadow: "none" }}>
      <CardContent>
        <Typography variant="h5" component="h1" gutterBottom>
          Accept the Clue2App NDA
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Before you can continue using the console, please review and accept
          the current Non-Disclosure Agreement.
        </Typography>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress data-testid="nda-loading" />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="nda-error">
            {error}
          </Alert>
        )}

        {!loading && nda && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              Version:{" "}
              <strong
                data-testid="nda-display-version"
                title={`Content hash: ${nda.version}`}
              >
                {nda.displayVersion ||
                  (nda.version ? nda.version.slice(0, 7) : "")}
              </strong>
              {nda.effectiveDate ? (
                <>
                  {" "}
                  &middot; Effective:{" "}
                  <span data-testid="nda-effective">{nda.effectiveDate}</span>
                </>
              ) : null}
            </Typography>

            {/* SCK-719: "Full text" link removed. The entire NDA body
                is rendered below (the user must scroll to the end
                before accept), so a secondary link to the raw text was
                redundant and visually noisy. */}

            {bodyIsSpaShell && (
              <Alert
                severity="warning"
                sx={{ mt: 2 }}
                data-testid="nda-body-unavailable"
              >
                NDA text is loading. If this persists, contact support.
              </Alert>
            )}

            {displayText && (
              <Box
                data-testid="nda-text"
                sx={{
                  mt: 2,
                  p: 2,
                  maxHeight: 320,
                  overflowY: "auto",
                  overflowX: "hidden",
                  background: "rgba(0,0,0,0.04)",
                  borderRadius: 1,
                  fontSize: "0.875rem",
                  // Constrain any first-child top margin so the body
                  // sits flush against the scroller top.
                  "& > :first-of-type": { mt: 0 },
                }}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={MD_COMPONENTS}
                >
                  {displayText}
                </ReactMarkdown>
              </Box>
            )}

            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  inputProps={{ "data-testid": "nda-checkbox" }}
                />
              }
              label="I have read and accept the NDA"
            />
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: "flex-end", p: 2 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={loading || submitting || !nda || !checked}
          onClick={handleAccept}
          data-testid="nda-accept"
        >
          {submitting ? "Recording…" : "Accept and continue"}
        </Button>
      </CardActions>
    </Card>
  );
}
